import React, { useState, useEffect } from "react";

import classnames from "classnames";
import {
  useGetCurrentCompanyApplicationQuery,
  useUpdateCompanyApplicationMutation,
  useGetLifePlansQuery,
  useUpdateUserExperienceFlagsMutation,
  useGetUserExperienceFlagsQuery,
} from "EmployerApplication/api";
import { UNSUPPORTED_UHC_SIC_CODES } from "EmployerApplication/Components/Constants";
import { SICCodeSearch } from "EmployerApplication/Components/FinalFormQuestions";
import createDecorator from "final-form-focus";
import isIntuit from "Intuit/isIntuit";
import { isRenewal } from "QuotePage/urls";
import { Form } from "react-final-form";
import { useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import Button from "Cobalt/Button";
import { TitleSmall } from "Cobalt/Header";
import { HorizontalLoading } from "Cobalt/HorizontalLoading";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "Cobalt/Modal";
import BodyText from "Common/BodyText";
import { Row } from "Common/Grid";
import Icon from "Common/Icon";

import LifePlanCard from "./LifePlanCard";

import makeBem from "Utils/makeBem";

const bem = makeBem("AddAncillaryPlanModal");
const focusOnErrors = createDecorator();

function SICPage({ submitHandler, initialValues, onHide, shouldPrefillSICCode }) {
  const isUnsupportedSICCode = (values) => [...UNSUPPORTED_UHC_SIC_CODES, "8999"].includes(values?.company_application?.sic_code);

  return (
    <Form onSubmit={submitHandler} decorators={[focusOnErrors]} initialValues={initialValues} keepDirtyOnReinitialize>
      {({ handleSubmit, submitting, form }) => (
        <form onSubmit={handleSubmit} method="post">
          <ModalBody className={isIntuit() ? classnames(bem("body-intuit"), bem("sicLifePage")) : classnames(bem("body"), bem("sicLifePage"))}>
            <TitleSmall mt={0} mb={2}>
              Enter keywords that describe your business and select the best match
            </TitleSmall>
            <BodyText pb={4} ml={1}>
              The pricing for life insurance varies depending on the nature and risk profile of the business.
              <br />
              Select from the list of standard industrial classification (SIC) codes to receive an exact quote for your business.
            </BodyText>
            <SICCodeSearch componentProps={{ shouldPrefillSICCode }} />
          </ModalBody>
          <ModalFooter>
            {isUnsupportedSICCode(form.getState().values) ? (
              <Button type="button" color="primary" onClick={onHide}>
                Close
              </Button>
            ) : (
              <Button type="submit" color="primary" disabled={submitting}>
                Continue
              </Button>
            )}
          </ModalFooter>
        </form>
      )}
    </Form>
  );
}

function PlansPage({ backHandler, onHide }) {
  const companyIDToken = useSelector((state) => state.companyProfile?.idToken);
  const useEmployeeForms = isRenewal();
  const companyZipCode = useSelector((state) => state.companyProfile?.zip_code_number);

  const employeeProfile = useSelector((state) => state.employeeProfile);
  const targetEffectiveDate = useSelector((state) => state.companyProfile?.targetEffectiveDate);

  const { data, isFetching: isFetchingLifePlans } = useGetLifePlansQuery({
    companyIDToken,
    useEmployeeForms,
    companyZipCode,
    employeeProfile,
    targetEffectiveDate,
  });

  const plans = data?.results;

  return (
    <>
      <ModalBody className={isIntuit() ? bem("body-intuit") : bem("body")}>
        {!isFetchingLifePlans && plans && plans.length === 0 ? (
          <>
            <TitleSmall mt={0} mb={2}>
              There are no life insurance plans available at this time.
            </TitleSmall>
            <BodyText mb={4} ml={1} weight="bold">
              It could be that life insurance plans are not offered for your business type <br /> or in the region where your business is located.
            </BodyText>
            <BodyText mb={4} ml={1} weight="bold">
              Please contact <a href="https://get.simplyinsured.com/contact">get.simplyinsured.com/contact</a> for more information.
            </BodyText>
          </>
        ) : (
          <>
            <TitleSmall mt={0} mb={2}>
              Select from the plans below.
            </TitleSmall>
            <BodyText mb={4} ml={1}>
              All Life Insurance plans require that the monthly premium is 100% covered by the employer. Employees must work full-time hours to be eligible for
              life insurance.
            </BodyText>
            {isFetchingLifePlans ? (
              <HorizontalLoading isLoading title="Loading plans..." />
            ) : (
              <Row>{plans?.length && plans.map((plan) => <LifePlanCard key={plan.id} plan={plan} onHide={onHide} />)}</Row>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="link" className="pull-left" onClick={backHandler}>
          <span>
            <Icon icon="chevron-left" pr={3} />
            Back
          </span>
        </Button>
      </ModalFooter>
    </>
  );
}

export default function LifePlansModal({ show, onHide }) {
  const [lifePlansPage, setLifePlansPage] = useState("categories");
  const [updateCompanyApplication] = useUpdateCompanyApplicationMutation();
  const [updateUserExperienceFlags] = useUpdateUserExperienceFlagsMutation();

  const handleSubmitOnClose = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      label: "closed_add_life_plan_modal",
    });
  };

  const { data: companyApplication, isLoading } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;
  const companyIDToken = useSelector((state) => state.companyProfile?.idToken);

  const { data: userExperienceFlags } = useGetUserExperienceFlagsQuery({
    companyIDToken,
  });

  // for renwals, we should prefill the sic code if the user has already entered it only if it was entered using the sic code modal
  // this will allow more specific sic codes to be entered.
  const shouldPrefillSICCode = (isRenewal() && userExperienceFlags?.life_plan_modal_sic_code_entered) || (!isRenewal() && companyApplication?.sicCode);

  const sicSubmitHandler = async (values) => {
    if (!companyPolicyId) return;
    await updateUserExperienceFlags({
      companyIDToken,
      life_plan_modal_sic_code_entered: true,
    });
    await updateCompanyApplication({ id: companyPolicyId, ...values });
    setLifePlansPage("plans");
  };

  let sicInitialValues;

  if (!isLoading) {
    sicInitialValues = {
      company_application: {
        business_category: companyApplication.businessCategory || undefined,
        sic_code: companyApplication.sicCode || undefined,
      },
    };
  }

  const hasSICCode = sicInitialValues?.company_application?.sic_code && userExperienceFlags?.life_plan_modal_sic_code_entered;

  useEffect(() => {
    if (hasSICCode) {
      setLifePlansPage("plans");
    }
  }, [show, hasSICCode]);

  return (
    <Modal
      show={show}
      onHide={() => {
        handleSubmitOnClose();
        onHide();
      }}
      // note: onShow used to be defined but it was always no-op, even before new modals
    >
      <ModalHeader closeButton className={isIntuit() && bem("header-intuit")}>
        <TitleSmall mt={0} mb={0}>
          Add Life Plan
        </TitleSmall>
      </ModalHeader>
      {lifePlansPage == "plans" ? (
        <PlansPage
          backHandler={() => {
            setLifePlansPage("categories");
          }}
          onShow={show}
          onHide={onHide}
        />
      ) : (
        <SICPage submitHandler={sicSubmitHandler} initialValues={sicInitialValues} onHide={onHide} shouldPrefillSICCode={shouldPrefillSICCode} />
      )}
    </Modal>
  );
}
